import { useMemo, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../components/avatar/Avatar';

const normalizeSearch = (value = '') => (
    String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
);

/**
 * Filtro de maestros del calendario (director y desarrollador).
 * Incluye búsqueda y lista con scroll a partir de 3 filas visibles.
 */
export const TeacherFilter = ({
    teacherOptions = [],
    selectedTeacherIds = [],
    onToggleTeacher,
    onClearTeachers,
}) => {
    const [query, setQuery] = useState('');
    const selectedSet = new Set(selectedTeacherIds.map(String));

    const filteredTeachers = useMemo(() => {
        const needle = normalizeSearch(query);
        if (!needle) return teacherOptions;

        return teacherOptions.filter((teacher) => {
            const haystack = normalizeSearch([
                teacher.name,
                teacher.firstName,
                teacher.lastName,
            ].filter(Boolean).join(' '));
            return haystack.includes(needle);
        });
    }, [teacherOptions, query]);

    const hasTeachers = teacherOptions.length > 0;
    const hasMatches = filteredTeachers.length > 0;
    const listScrollable = filteredTeachers.length > 3;

    return (
        <section className="cal-sidebar-panel cal-teacher-filter" aria-labelledby="cal-teacher-filter-title">
            <div className="cal-sidebar-panel__header cal-teacher-filter__header">
                <h3 id="cal-teacher-filter-title" className="cal-sidebar-panel__title">
                    Maestros
                </h3>
                {selectedTeacherIds.length > 0 && (
                    <button
                        type="button"
                        onClick={onClearTeachers}
                        className="cal-teacher-filter__clear"
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {hasTeachers ? (
                <label className="cal-teacher-filter__search">
                    <MagnifyingGlassIcon className="cal-teacher-filter__search-icon" aria-hidden="true" />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Buscar maestro…"
                        className="cal-teacher-filter__search-input"
                        aria-label="Buscar maestro"
                        autoComplete="off"
                    />
                </label>
            ) : null}

            {!hasTeachers ? (
                <p className="cal-teacher-filter__empty">No hay profesores.</p>
            ) : !hasMatches ? (
                <p className="cal-teacher-filter__empty">No hay profesores.</p>
            ) : (
                <ul
                    className={`cal-sidebar-panel__list cal-teacher-filter__list${
                        listScrollable ? ' cal-teacher-filter__list--scroll' : ''
                    }`}
                >
                    {filteredTeachers.map((teacher) => {
                        const checked = selectedSet.has(String(teacher.id));
                        const inputId = `cal-teacher-${teacher.id}`;

                        return (
                            <li key={teacher.id}>
                                <label
                                    htmlFor={inputId}
                                    className={`cal-sidebar-row cal-teacher-filter__option${checked ? ' is-selected' : ''}`}
                                >
                                    <input
                                        id={inputId}
                                        type="checkbox"
                                        className="cal-teacher-filter__input"
                                        checked={checked}
                                        onChange={() => onToggleTeacher(teacher.id)}
                                    />
                                    <span className="cal-sidebar-row__icon cal-sidebar-row__icon--avatar" aria-hidden="true">
                                        {teacher.firstName || teacher.lastName ? (
                                            <Avatar
                                                size="sm"
                                                firstName={teacher.firstName}
                                                lastName={teacher.lastName}
                                                className="cal-teacher-filter__avatar"
                                            />
                                        ) : (
                                            <UserIcon className="cal-teacher-filter__avatar-fallback" />
                                        )}
                                    </span>
                                    <span className="cal-sidebar-row__label" title={teacher.name}>
                                        {teacher.name}
                                    </span>
                                    <span className="cal-sidebar-row__slot">
                                        <span className={`cal-teacher-filter__check${checked ? ' is-selected' : ''}`}>
                                            {checked ? (
                                                <CheckIcon className="cal-teacher-filter__check-icon" />
                                            ) : null}
                                        </span>
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            )}

            <p className="cal-teacher-filter__hint">
                Sin selección se muestran todos los maestros.
            </p>
        </section>
    );
};
